/* Keychron series guide (sections/keychron-guide.liquid).
   Renders the series table, filters, compare panel and board cards from the catalogue's public export. */
(() => {
  const el = (tag, props = {}, ...children) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(props)) {
      if (key === 'class') node.className = value;
      else if (key === 'text') node.textContent = value;
      else if (key.startsWith('on')) node.addEventListener(key.slice(2), value);
      else if (value !== undefined && value !== null && value !== false) node.setAttribute(key, value === true ? '' : value);
    }
    children.flat().forEach((child) => child !== null && child !== undefined && child !== false && node.append(child));
    return node;
  };
  const ext = (href, props, ...children) => el('a', { href, target: '_blank', rel: 'noopener', ...props }, ...children);
  const cellValue = (raw) => (raw && typeof raw === 'object' ? raw.value : raw);
  // What the comparison compares: the value without bracketed extras, so "Aluminum (one copper edition)" matches "Aluminum".
  const mainValue = (raw) => String(cellValue(raw) || '').replace(/\s*\([^)]*\)/g, '').trim().toLowerCase();
  const cellNote = (raw) => (raw && typeof raw === 'object' ? raw.note : null);
  // Keychron's images are 2000px originals; Shopify's CDN can serve a small copy instead.
  const thumbUrl = (src) => {
    try {
      const url = new URL(src);
      if (url.hostname.endsWith('cdn.shopify.com') || url.pathname.includes('/cdn/shop/')) url.searchParams.set('width', '240');
      return url.href;
    } catch (e) { return src; }
  };
  const familyName = (f) => `${f.brand === 'Lemokey' ? 'Lemokey ' : ''}${f.name} series`;

  // Columns listing several things separated by " · ", shown one per line.
  const MULTILINE = new Set(['connection']);
  const WIDTHS = { hot_swap: 'w-xs', plate: 'w-s', mount: 'w-s', battery: 'w-s', orientation: 'w-m', magnet_pole: 'w-s', switches: 'w-m', case: 'w-m', case_access: 'w-m', software: 'w-m', connection: 'w-l' };
  const STORE_KEY = 'kk-guide-collapsed';
  const VIEW_KEY = 'kk-guide-view';  // this tab's search, filters, open series and comparison
  // Columns that only apply to some series: shown as "—" (not "Not listed") elsewhere.
  const NOT_APPLICABLE = { magnet_pole: (s) => !/magnetic/i.test(cellValue(s.comparables.switches) || '') };

  function start(root, DATA) {
    const $ = (sel) => root.querySelector(sel);
    const columns = DATA.columns.filter((c) => c.display !== false);
    const familyById = Object.fromEntries(DATA.families.map((f) => [f.id, f]));
    const seriesIds = new Set(DATA.series.map((s) => s.id));
    let compareTouched = false;
    const state = { family: '', query: '', switch: '', connection: '', polling: '', open: new Set(), compare: [], collapsed: new Set() };
    try { JSON.parse(localStorage.getItem(STORE_KEY) || '[]').forEach((id) => state.collapsed.add(id)); } catch (e) { /* storage unavailable */ }
    const saveCollapsed = () => { try { localStorage.setItem(STORE_KEY, JSON.stringify([...state.collapsed])); } catch (e) { /* ignore */ } };
    const widthClass = (id) => WIDTHS[id] || 'w-m';
    const searchText = (s) => [s.name, s.tagline, ...s.models.map((m) => m.model), ...s.models.flatMap((m) => (m.layouts || []).map((l) => (typeof l === 'string' ? l : l.layout))), ...Object.values(s.comparables).map(cellValue)].filter(Boolean).join(' ').toLowerCase();

    function renderValue(raw, columnId, s) {
      const value = cellValue(raw);
      if (!value && s && NOT_APPLICABLE[columnId] && NOT_APPLICABLE[columnId](s)) return el('span', { class: 'kk-guide__unknown', text: '—', title: 'Not applicable to this series' });
      const note = cellNote(raw);
      const span = !value
        ? el('span', { class: 'kk-guide__unknown', text: 'Not listed', title: "Keychron doesn't state this for the series" })
        : MULTILINE.has(columnId)
          ? el('span', { class: 'kk-guide__lines' }, ...value.split(' · ').map((line) => el('span', { text: line, class: /\b8K\b/.test(line) ? 'kk-guide__8k' : undefined })))
          : el('span', { text: value, class: /\b8K\b/.test(value) ? 'kk-guide__8k' : undefined });
      if (note) {
        span.classList.add('kk-guide__has-note');
        span.title = note;
      }
      return span;
    }

    function matches(s) {
      const c = (k) => String(cellValue(s.comparables[k]) || '').toLowerCase();
      if (state.family && s.family !== state.family) return false;
      if (state.query && !state.query.split(/\s+/).every((word) => searchText(s).includes(word))) return false;
      if (state.switch && !c('switches').includes(state.switch)) return false;  // Mechanical/Magnetic matches both
      if (state.connection === 'wired' && c('connectivity') !== 'wired only') return false;
      if (state.connection === 'wireless' && !/bluetooth|2\.4/.test(c('connectivity'))) return false;
      if (state.polling && !c('polling').includes(state.polling)) return false;
      return true;
    }

    function renderFamilies() {
      $('[data-families]').replaceChildren(
        el('button', { class: 'kk-guide__chip', type: 'button', 'aria-pressed': String(!state.family), text: 'All series', onclick: () => { state.family = ''; render(); } }),
        ...DATA.families.map((f) =>
          el('button', { class: 'kk-guide__chip', type: 'button', 'aria-pressed': String(state.family === f.id), title: f.summary, text: familyName(f), onclick: () => { state.family = state.family === f.id ? '' : f.id; render(); } })
        )
      );
    }

    function renderHelp() {
      $('[data-help]').replaceChildren(...columns.flatMap((c) => [el('dt', { text: c.label }), el('dd', { text: c.help })]));
    }

    function renderHead() {
      $('[data-head]').replaceChildren(
        el('th', { class: 'kk-guide__name-col', scope: 'col', text: 'Series' }),
        ...columns.map((c) => el('th', { scope: 'col', class: widthClass(c.id), title: c.help, text: c.label }))
      );
    }

    function toggleFamily(id) {
      state.collapsed.has(id) ? state.collapsed.delete(id) : state.collapsed.add(id);
      saveCollapsed();
      renderBody();
    }

    function modelCard(m) {
      const img = m.image
        ? el('img', { class: 'kk-guide__thumb', src: thumbUrl(m.image), alt: '', loading: 'lazy', width: 96, height: 64, onerror: (e) => { e.target.replaceWith(el('span', { class: 'kk-guide__thumb' })); } })
        : el('span', { class: 'kk-guide__thumb' });
      const differs = m.differs || [];
      const highlights = m.highlights || [];
      // Layouts Keychron sells the board in, each linking to its own page (older data: plain names, no links).
      const layouts = (m.layouts || []).map((l) => (typeof l === 'string' ? { layout: l, url: null } : l));
      // The name links to the board's page and is stretched over the whole card; the layout chips are links above it.
      const name = m.product_url
        ? ext(m.product_url, { class: 'kk-guide__model-link', title: `${m.model} on keychron.com (opens in a new tab)` }, m.model, el('span', { class: 'kk-guide__ext', text: '↗', 'aria-hidden': 'true' }))
        : el('span', {}, m.model);
      const text = el('span', { class: 'kk-guide__model-text' },
        el('span', { class: 'kk-guide__model-name' }, name),
        el('span', { class: 'kk-guide__model-size', text: m.size || 'Size not listed' }),
        layouts.length ? el('span', { class: 'kk-guide__tags kk-guide__layouts' },
          ...layouts.map((l) => (l.url
            ? ext(l.url, { text: l.layout, title: `${m.model} ${l.layout} on keychron.com (opens in a new tab)` })
            : el('span', { text: l.layout })))) : null,
        m.on_sale === false ? el('span', { class: 'kk-guide__tags' }, el('span', { class: 'is-unreleased', text: 'Not available', title: "Keychron doesn't have a product page for this board." })) : null,
        differs.length || highlights.length ? el('span', { class: 'kk-guide__tags' },
          ...differs.map((h) => el('span', { class: 'is-different', text: h, title: 'Differs from the rest of the series' })),
          ...highlights.map((h) => el('span', { text: h }))) : null
      );
      return el('div', { class: `kk-guide__model${m.product_url ? ' has-link' : ''}` }, img, text);
    }

    function renderBody() {
      saveView();
      const body = $('[data-body]');
      const visible = DATA.series.filter(matches);
      $('[data-count]').textContent = `${visible.length} of ${DATA.series.length} series`;
      const allShut = DATA.families.every((f) => state.collapsed.has(f.id));
      $('[data-collapse-all]').textContent = allShut ? 'Expand all' : 'Collapse all';
      if (!visible.length) {
        body.replaceChildren(el('tr', {}, el('td', { class: 'kk-guide__empty', colspan: columns.length + 1, text: 'No series match these filters. Try clearing the search or a filter.' })));
        return;
      }
      const familyCount = {};
      visible.forEach((s) => { familyCount[s.family] = (familyCount[s.family] || 0) + 1; });
      const rows = [];
      let family = null;
      for (const s of visible) {
        if (s.family !== family) {
          family = s.family;
          const f = familyById[family] || { id: family, name: family };
          const shut = state.collapsed.has(f.id);
          rows.push(el('tr', { class: 'kk-guide__family-row' }, el('th', { colspan: columns.length + 1, scope: 'colgroup' },
            el('button', { class: 'kk-guide__family-toggle', type: 'button', 'aria-expanded': String(!shut), title: shut ? 'Show these series' : 'Hide these series', onclick: () => toggleFamily(f.id) },
              el('span', { class: 'kk-guide__chevron', 'aria-hidden': 'true', text: shut ? '▸' : '▾' }),
              familyName(f),
              el('span', { class: 'kk-guide__family-note', text: shut ? `${familyCount[f.id]} series hidden` : (f.summary || '') })))));
        }
        if (state.collapsed.has(s.family)) continue;
        const open = state.open.has(s.id);
        const unreleased = s.models.length && s.models.every((m) => m.on_sale === false);
        const name = el('span', { class: 'kk-guide__row-title' }, s.name,
          unreleased ? el('span', { class: 'kk-guide__unreleased', text: 'Not available', title: "Keychron doesn't have a product page for this board." }) : null);
        const head = el('th', { class: 'kk-guide__name-col', scope: 'row' },
          el('div', { class: 'kk-guide__row-head' },
            s.product_url ? ext(s.product_url, { class: 'kk-guide__series-link', title: `${s.name} on keychron.com (opens in a new tab)` }, name) : name,
            s.tagline ? el('div', { class: 'kk-guide__tagline', text: s.tagline }) : null,
            el('div', { class: 'kk-guide__row-tools' },
              el('button', { class: 'kk-guide__expand', type: 'button', 'aria-expanded': String(open), text: `${open ? 'Hide' : 'Show'} ${s.models.length} model${s.models.length === 1 ? '' : 's'}`, onclick: () => { open ? state.open.delete(s.id) : state.open.add(s.id); renderBody(); } }),
              el('label', {}, el('input', { type: 'checkbox', checked: state.compare.includes(s.id), onchange: (e) => toggleCompare(s.id, e.target.checked) }), 'Compare')
            )
          )
        );
        rows.push(el('tr', {}, head, ...columns.map((c) => el('td', { class: `kk-guide__cell ${widthClass(c.id)}` }, renderValue(s.comparables[c.id], c.id, s)))));
        if (open) {
          rows.push(el('tr', { class: 'kk-guide__detail-row' }, el('td', { colspan: columns.length + 1 },
            el('div', { class: 'kk-guide__detail' },
              el('h3', { text: `Models in ${s.name}` }),
              el('div', { class: 'kk-guide__models' }, ...s.models.map(modelCard))))));
        }
      }
      body.replaceChildren(...rows);
    }

    // The first series ticked is the baseline the others are compared against. A fifth tick drops the oldest
    // of the others, never the baseline; unticking the baseline makes the next one the baseline.
    function toggleCompare(id, on) {
      compareTouched = true;
      const rest = state.compare.filter((x) => x !== id);
      state.compare = !on ? rest : rest.length < 4 ? [...rest, id] : [rest[0], ...rest.slice(2), id];
      renderCompare();
      renderBody();
    }

    function renderCompare() {
      const box = $('[data-compare]');
      const picked = state.compare.map((id) => DATA.series.find((s) => s.id === id)).filter(Boolean);
      box.hidden = picked.length < 2;
      if (picked.length < 2) return;
      const [base] = picked;
      $('[data-compare-title]').textContent = `Comparing ${picked.map((s) => s.name).join(', ')}`;
      $('[data-compare-hint]').textContent = `Highlighted cells differ from ${base.name}, the first series you ticked.`;
      const rows = columns.map((c) => {
        const baseValue = mainValue(base.comparables[c.id]);
        const differs = picked.map((s) => mainValue(s.comparables[c.id]) !== baseValue);
        const any = differs.some(Boolean);
        return el('tr', { class: any ? 'is-different' : undefined },
          el('th', { scope: 'row', text: c.label, title: any ? `Differs from ${base.name} here` : undefined }),
          ...picked.map((s, i) => el('td', { class: differs[i] ? 'is-different' : undefined, title: differs[i] ? `Differs from ${base.name}` : undefined }, renderValue(s.comparables[c.id], c.id, s))));
      });
      rows.push(el('tr', {}, el('th', { scope: 'row', text: 'Models' }), ...picked.map((s) => el('td', { text: s.models.map((m) => m.model).join(', ') }))));
      $('[data-compare-table]').replaceChildren(
        el('thead', {}, el('tr', {}, el('th', { scope: 'col' }),
          ...picked.map((s, i) => el('th', { scope: 'col', class: i === 0 ? 'is-base' : undefined }, s.name, i === 0 ? el('span', { class: 'kk-guide__base-tag', text: 'Baseline' }) : null)))),
        el('tbody', {}, ...rows));
    }

    function render() {
      renderFamilies();
      renderBody();
    }

    $('[data-search]').addEventListener('input', (e) => { state.query = e.target.value.trim().toLowerCase(); renderBody(); });
    root.querySelectorAll('[data-filter]').forEach((select) => {
      select.addEventListener('change', (e) => { state[select.dataset.filter] = e.target.value; renderBody(); });
    });
    $('[data-collapse-all]').addEventListener('click', () => {
      const all = DATA.families.map((f) => f.id);
      state.collapsed = new Set(all.every((id) => state.collapsed.has(id)) ? [] : all);
      saveCollapsed();
      renderBody();
    });
    $('[data-clear-compare]').addEventListener('click', () => { compareTouched = true; state.compare = []; renderCompare(); renderBody(); });

    // Keep the view when the browser reloads the tab (e.g. a background tab it unloaded to save memory).
    // The comparison also goes in the address (#compare=q-max,q-he) so it survives reloads and can be shared.
    function saveView() {
      try {
        sessionStorage.setItem(VIEW_KEY, JSON.stringify({ family: state.family, search: $('[data-search]').value, switch: state.switch,
          connection: state.connection, polling: state.polling, open: [...state.open], compare: state.compare }));
      } catch (e) { /* storage unavailable */ }
      if (!compareTouched && !location.hash.startsWith('#compare=')) return;  // leave the address alone until the visitor compares
      const hash = state.compare.length ? `#compare=${state.compare.join(',')}` : '';
      if (hash !== location.hash && !location.hash.startsWith('#kk-guide-report')) {
        try { history.replaceState(history.state, '', location.pathname + location.search + hash); } catch (e) { /* ignore */ }
      }
    }
    const validIds = (ids) => (ids || []).map((x) => String(x).trim()).filter((id) => seriesIds.has(id));
    let saved = null;
    try { saved = JSON.parse(sessionStorage.getItem(VIEW_KEY) || 'null'); } catch (e) { /* storage unavailable */ }
    const fromHash = location.hash.startsWith('#compare=') ? validIds(decodeURIComponent(location.hash.slice(9)).split(',')).slice(0, 4) : null;
    if (saved) {
      state.family = familyById[saved.family] ? saved.family : '';
      $('[data-search]').value = saved.search || '';
      state.query = (saved.search || '').trim().toLowerCase();
      for (const key of ['switch', 'connection', 'polling']) state[key] = saved[key] || '';
      root.querySelectorAll('[data-filter]').forEach((select) => { select.value = state[select.dataset.filter]; });
      state.open = new Set(validIds(saved.open));
    }
    state.compare = fromHash || (saved ? validIds(saved.compare).slice(0, 4) : validIds((root.dataset.example || '').split(',')).slice(0, 4));
    if (DATA.checked_at) $('[data-checked]').textContent = `Last checked against Keychron's site ${new Date(DATA.checked_at + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`;
    // Corrections form: list the series by family so a report names the right one.
    const reportSelect = root.querySelector('[data-report-series]');
    if (reportSelect && reportSelect.options.length < 2) {
      DATA.families.forEach((f) => {
        const group = el('optgroup', { label: familyName(f) });
        DATA.series.filter((s) => s.family === f.id).forEach((s) => group.append(el('option', { value: s.name, text: s.name })));
        if (group.children.length) reportSelect.append(group);
      });
    }
    renderHelp();
    renderHead();
    render();
    renderCompare();
  }

  async function load(root) {
    const inline = root.querySelector('[data-guide-data]');
    try {
      const data = inline ? JSON.parse(inline.textContent) : await fetch(root.dataset.src).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });
      start(root, data);
    } catch (e) {
      root.querySelector('[data-body]').replaceChildren(el('tr', {}, el('td', { class: 'kk-guide__empty', text: "The guide couldn't load. Please refresh the page." })));
      console.error('Keychron guide:', e);
    }
  }

  document.querySelectorAll('[data-keychron-guide]').forEach(load);
  // Theme editor: re-render when the section is added or its settings change.
  document.addEventListener('shopify:section:load', (e) => e.target.querySelectorAll('[data-keychron-guide]').forEach(load));
})();
